import { useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import type { SelectChangeEvent } from '@mui/material/Select';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';

interface SubNavEntry {
    /** Sub-path appended to `/account/`. */
    path: string;
    /** Display label rendered in the tab. */
    label: string;
}

/**
 * Personal-settings sub-nav entries (issue #2114). Every authenticated user —
 * admin or signer — sees all four panels. Each entry mounts under
 * `/account/<path>`.
 */
const ACCOUNT_ENTRIES: SubNavEntry[] = [
    { path: 'appearance', label: 'Appearance' },
    { path: 'profile', label: 'Profile' },
    { path: 'notifications', label: 'Notifications' },
    { path: 'security', label: 'Security' },
];

/**
 * Parse the active sub-path from the URL so the correct tab renders selected.
 * Returns `null` when the URL is bare `/account` (the index redirect handles
 * landing in that case — tabs only render once we have a real section).
 */
function parseAccountSub(pathname: string): string | null {
    const stripped = pathname.replace(/^\/account\/?/, '');
    if (stripped === '') {
        return null;
    }
    const [segment] = stripped.split('/');
    return segment || null;
}

/**
 * Personal Settings page shell (#2114; split out of the original `Settings`
 * shell from #2076).
 *
 * Renders a single sub-navigation rail with FOUR tabs — Appearance, Profile,
 * Notifications, Security — plus an `<Outlet />` for the active panel. Every
 * authenticated user — admin or signer — can reach this shell; there is no
 * `<AdminRoute>` gate around it in App.tsx. Workspace-level settings live on
 * their own `/settings/*` shell (`<WorkspaceSettings />`) which IS admin-only.
 *
 * Entry points:
 * - The user-avatar menu's "Personal Settings" item (every user).
 * - Direct URLs: `/account/appearance`, `/account/profile`, etc.
 * - Bare `/account` index-redirects to `/account/profile` in App.tsx.
 */
export default function AccountSettings() {
    const navigate = useNavigate();
    const location = useLocation();

    const muiTheme = useMuiTheme();
    const isDesktop = useMediaQuery(muiTheme.breakpoints.up('sm'));

    const sub = useMemo(() => parseAccountSub(location.pathname), [location.pathname]);

    // `false` means "no tab selected" — the bare `/account` URL is handled by
    // an index Navigate in App.tsx before we land here with a real sub-path.
    const value: string | false = sub
        ? ACCOUNT_ENTRIES.find((e) => e.path === sub)?.path ?? false
        : false;

    const handleTabChange = (_event: React.SyntheticEvent, next: string) => {
        navigate(`/account/${next}`);
    };

    // #2170 — mobile Select handler. At xs viewport the 4 tabs (Appearance,
    // Profile, Notifications, Security) overflowed the scrollable rail; a
    // Select dropdown shows every destination on a single line per option
    // without truncation. Matches the Linear / Stripe mobile settings idiom.
    const handleSelectChange = (event: SelectChangeEvent<string>) => {
        navigate(`/account/${event.target.value}`);
    };
    const selectValue = (value || ACCOUNT_ENTRIES[0].path) as string;

    return (
        <Box className="page-content">
            <Typography variant="h4" component="h1" gutterBottom>
                Personal Settings
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 3 },
                    alignItems: 'stretch',
                }}
            >
                <Box
                    sx={{
                        flexShrink: 0,
                        width: { xs: '100%', sm: 220 },
                        borderRight: { sm: 1 },
                        borderBottom: { xs: 0, sm: 0 },
                        borderColor: 'divider',
                    }}
                >
                    <Box component="nav" aria-label="Account settings">
                        {isDesktop ? (
                            <Tabs
                                orientation="vertical"
                                variant="scrollable"
                                scrollButtons={true}
                                allowScrollButtonsMobile
                                value={value}
                                onChange={handleTabChange}
                                aria-label="Account settings"
                                sx={{
                                    '& .MuiTab-root': {
                                        alignItems: 'flex-start',
                                        textAlign: 'left',
                                        textTransform: 'none',
                                        fontSize: '0.95rem',
                                    },
                                }}
                            >
                                {ACCOUNT_ENTRIES.map((entry) => (
                                    <Tab key={entry.path} value={entry.path} label={entry.label} />
                                ))}
                            </Tabs>
                        ) : (
                            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                                <InputLabel id="account-settings-section-label">Personal settings section</InputLabel>
                                <Select
                                    labelId="account-settings-section-label"
                                    id="account-settings-section"
                                    label="Personal settings section"
                                    value={selectValue}
                                    onChange={handleSelectChange}
                                >
                                    {ACCOUNT_ENTRIES.map((entry) => (
                                        <MenuItem key={entry.path} value={entry.path}>
                                            {entry.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
}
